import { BookmarkSimple, ChatCircle, DotsThreeVertical, Heart } from '@phosphor-icons/react';
import type { Comment } from '@sirocco/shared';
import { Link, useNavigate } from 'react-router-dom';
import { posts as postsApi } from '../../api/endpoints';
import { Avatar } from '../../components/ui/Avatar';
import { PostText } from '../../components/ui/PostText';
import { useSession } from '../../store/SessionContext';
import { timeAgo } from '../../utils/format';
import { profilePath } from '../../utils/routes';
import { t } from '../../i18n';

/**
 * Une reponse, telle qu'elle se lit partout.
 *
 * Le fil d'un post et le fil d'une reponse montrent le meme objet : il
 * n'y a pas de « reponse de deuxieme rang » qui se lirait autrement. Une
 * seule ligne pour les deux ecrans, donc — deux copies auraient fini par
 * diverger d'un pixel, puis d'un bouton.
 *
 * Elle mene toujours a son propre fil. C'est ce qui rend la profondeur
 * sans fin sans jamais rien decaler : on descend d'un cran a chaque
 * appui, et l'ecran est toujours le meme.
 */
export function CommentRow({
  comment,
  onChange,
  onMenu,
}: {
  comment: Comment;
  onChange: (comment: Comment) => void;
  onMenu: (trigger: HTMLElement) => void;
}) {
  const { user } = useSession();
  const navigate = useNavigate();
  const authorPath = profilePath(comment.author.id, user?.id);
  const filPath = `/app/feed/${comment.postId}/comments/${comment.id}`;

  /*
    Like et signet repondent au doigt, puis se confirment — comme sur un
    post. Un aller-retour avant de peindre ferait hesiter le coeur.
  */
  async function toggle(next: Comment, save: (comment: Comment) => Promise<Comment>) {
    onChange(next);
    try {
      onChange(await save(comment));
    } catch {
      onChange(comment);
    }
  }

  return (
    /*
      La ligne entiere ouvre le fil de la reponse, comme un post s'ouvre
      depuis le feed. Les gestes qui ont deja leur bouton — le profil, le
      coeur, le signet, le menu — arretent la propagation eux-memes.
    */
    <article
      className="cm-item cm-item--ouvrable"
      data-commentaire={comment.id}
      role="button"
      tabIndex={0}
      onClick={() => navigate(filPath, { state: { comment } })}
      onKeyDown={(touche) => {
        if (touche.key !== 'Enter' && touche.key !== ' ') return;
        touche.preventDefault();
        navigate(filPath, { state: { comment } });
      }}
    >
      <Link
        className="cm-item__avatar"
        to={authorPath}
        aria-label={comment.author.username}
        onClick={(clic) => clic.stopPropagation()}
      >
        <Avatar user={comment.author} size={32} />
      </Link>

      <div className="cm-item__body">
        <p className="cm-item__head">
          <Link
            to={authorPath}
            className="cm-item__author"
            onClick={(clic) => clic.stopPropagation()}
          >
            {comment.author.username}
          </Link>
          <span className="cm-item__time">· {timeAgo(comment.createdAt)}</span>
        </p>

        <PostText
          content={comment.content}
          mentions={comment.mentions}
          className="cm-item__content"
        />

        <footer className="cm-item__actions" onClick={(clic) => clic.stopPropagation()}>
          {/*
            La bulle en premier, comme sur une carte du fil : repondre est
            le geste qu'on cherche, aimer vient apres. Le meme ordre d'un
            ecran a l'autre, donc le pouce sait ou aller sans lire.

            Jamais pleine, quel que soit le nombre : une bulle remplie dit
            partout ailleurs « c'est le tien » ou « tu l'as fait ». Ici
            elle ne compte que ce que d'autres ont ecrit, et la remplir
            aurait promis une appartenance qui n'existe pas.

            Le compte porte tous les descendants et non les seuls enfants
            directs : une reponse a une reponse appartient au meme fil, et
            ne pas la compter ferait annoncer « 1 » a un fil qui en porte
            six.
          */}
          <button
            className="cm-item__action"
            aria-label={
              comment.replyCount > 0
                ? t.fil.reponse.voirReponses(comment.replyCount)
                : t.fil.reponse.repondreA(comment.author.username)
            }
            onClick={() => navigate(filPath, { state: { comment } })}
          >
            <ChatCircle size={15} />
            {comment.replyCount > 0 && <span>{comment.replyCount}</span>}
          </button>

          <button
            className={`cm-item__action${comment.liked ? ' cm-item__action--liked' : ''}`}
            aria-label={comment.liked ? t.fil.commun.retirerLike : t.fil.commun.likerReponse}
            aria-pressed={comment.liked}
            onClick={() =>
              void toggle(
                {
                  ...comment,
                  liked: !comment.liked,
                  likeCount: Math.max(0, comment.likeCount + (comment.liked ? -1 : 1)),
                },
                postsApi.likeComment,
              )
            }
          >
            <Heart size={15} weight={comment.liked ? 'fill' : 'regular'} />
            {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
          </button>

          <button
            className={`cm-item__action${comment.bookmarked ? ' cm-item__action--saved' : ''}`}
            aria-label={comment.bookmarked ? t.fil.commun.retirerSignet : t.fil.commun.enregistrer}
            aria-pressed={comment.bookmarked}
            onClick={() =>
              void toggle({ ...comment, bookmarked: !comment.bookmarked }, postsApi.bookmarkComment)
            }
          >
            <BookmarkSimple size={15} weight={comment.bookmarked ? 'fill' : 'regular'} />
            {comment.bookmarkCount > 0 && <span>{comment.bookmarkCount}</span>}
          </button>

          <button
            className="cm-item__menu"
            aria-label={t.fil.commun.actionsReponse}
            onClick={(pressed) => onMenu(pressed.currentTarget)}
          >
            <DotsThreeVertical size={18} weight="bold" />
          </button>
        </footer>
      </div>
    </article>
  );
}
